import React, { useEffect, useState } from 'react';
import { Calendar, CreditCard } from 'lucide-react';
import { useStore, Transaction } from '../../store/useStore';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../lib/firebase';

const SubscriptionHistory: React.FC = () => {
  const cancelSubscription = useStore((state) => state.cancelSubscription);
  const subscriptionHistory = useStore((state) => state.subscriptionHistory);
  const user = useStore((state) => state.user);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [firstPayment, setFirstPayment] = useState<Transaction | null>(null);
  const [nextBillingDate, setNextBillingDate] = useState<string | null>(null);
  const [lastPaymentAmount, setLastPaymentAmount] = useState<number | null>(null);
  const [lastPaymentCard, setLastPaymentCard] = useState<string | null>(null);
  const [lastPaymentBrand, setLastPaymentBrand] = useState<string | null>(null);
  const subscriptionAmount = useStore((state) => state.subscriptionAmount || 10.99);
  const [isLoading, setIsLoading] = useState(true);
  const updateUserData = useStore((state) => state.updateUserData);
  // const [lastSessionId, setLastSessionId] = useState<string | undefined>();
  const [theme, setTheme] = useStore((state) => [
    state.userPreferences?.theme || 'dark',
    state.updatePreferences
  ]);
  const [couponId, setCouponId] = useState<any>();
  const [referralId, setReferralId] = useState<any>();

  useEffect(() => {
    updateUserData();
    if (user?.referredBy) {
      setReferralId(user?.referredBy);
    }
    if (user?.couponId) {
      setCouponId(user?.couponId);
    }
  }, []);

  useEffect(() => {
    const fetchHistory = async () => {
      if (user) {
        setIsLoading(true);

        // const lastSessionId =  userData?.lastSessionId;
        // setLastSessionId(lastSessionId);
        const subscriptionHistoryTmp = subscriptionHistory;
        if (subscriptionHistoryTmp) {
          console.log("Processing Transaction", user?.stripeCustomerId);
          let sortedTransactions: Transaction[] = [];
          try {
            const subscription = subscriptionHistoryTmp?.subscriptions?.[0];

            if (subscription) {
              console.log(subscription.currentPeriodEnd);
              setNextBillingDate(
                subscription.currentPeriodEnd
                  ? typeof subscription.currentPeriodEnd === 'string'
                    ? subscription.currentPeriodEnd
                    : subscription.currentPeriodEnd instanceof Date
                      ? subscription.currentPeriodEnd.toISOString()
                      : null
                  : null
              );
              setLastPaymentCard(subscription.defaultPaymentMethod?.cardLast4 ?? null);
              setLastPaymentBrand(subscription.defaultPaymentMethod?.brand ?? null);
            } else {
              setNextBillingDate(null);
              setLastPaymentCard(null);
              setLastPaymentBrand(null);
            }

            setLastPaymentAmount(subscriptionHistoryTmp?.amount ?? null);
          } catch (error) {
            console.error('Error setting subscription details:', error);
            // Set safe fallbacks
            setNextBillingDate(null);
            setLastPaymentAmount(null);
            setLastPaymentCard(null);
            setLastPaymentBrand(null);
          }

          const transactionsTmp: Transaction[] = subscriptionHistoryTmp.subscriptions.map((transaction: any) => ({
            id: transaction.id,
            date: transaction.currentPeriodStart || new Date().toISOString(),
            amount: transaction.amount,
            cardLast4: transaction.defaultPaymentMethod.cardLast4,
            status: transaction.status,

          }));

          sortedTransactions = [...transactionsTmp].sort(
            (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
          );
          setTransactions(transactionsTmp);
          // console.log(subscriptionHistoryTmp, transactionsTmp, transactions, lastPaymentAmount, lastPaymentBrand, lastPaymentCard, nextBillingDate);
          const firstPayment = sortedTransactions[sortedTransactions.length - 1];
          setFirstPayment(firstPayment);
        }
      }
    };

    fetchHistory();
    setIsLoading(false);
  }, [user]);

  // Theme handling
  useEffect(() => {
    if (!theme) {
      const newTheme = localStorage.getItem('theme') || 'dark';
      if (newTheme === 'light' || newTheme === 'dark') {
        setTheme({ theme: newTheme });
      }
    }
  }, [theme]);

  useEffect(() => {
    if (!couponId) {
      if (new URLSearchParams(window.location.search).get('coupon_id')) {
        const couponId = new URLSearchParams(window.location.search).get('coupon_id');
        setCouponId(couponId);
      }
    }
  })

  useEffect(() => {
    if (!referralId) {
      if (new URLSearchParams(window.location.search).get('referral_id')) {
        const referralId = new URLSearchParams(window.location.search).get('referral_id');
        setReferralId(referralId);
      }
    }
  })

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-secondary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Subscription</h1>
        {!(nextBillingDate && new Date(nextBillingDate).getTime() >= new Date().setHours(0, 0, 0, 0)) &&
          (<form action="https://subscriptionservice-bcnavzhxvq-uc.a.run.app/subscription/create-checkout-session" method="POST">
            <input type="hidden" name="price_id" value="price_1R0ovtGv5zuz8MKh5ndYucI6" />
            <input type="hidden" name="email" value={user?.email} />
            <input type="hidden" name="client_reference_id" value={user?.id} />
            <input type="hidden" name="coupon_id" value={couponId} />
            <input type="hidden" name="stripe_customer_id" value={user?.stripeCustomerId} />
            <input type="hidden" name="referral_id" value={referralId} />
            <button type="submit" className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
              Subscribe
            </button>
          </form>)
        }
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between pb-6 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Pro Plan</h2>
            <p className="text-gray-600">
              Next billing date: {
                nextBillingDate && new Date(nextBillingDate).getTime() >= new Date().setHours(0, 0, 0, 0)
                  ? new Date(nextBillingDate).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })
                  : 'N/A'
              }
            </p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-gray-800">£{lastPaymentAmount ? lastPaymentAmount : subscriptionAmount}/month</p>
            <p className="text-gray-600">
              Active since {firstPayment?.date ? new Date(firstPayment.date).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              }) : 'N/A'}
            </p>
          </div>
        </div>

        <div className="mt-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Payment History</h3>
          <div className="space-y-4">
            {transactions.length > 0 ? (
              transactions.map((transaction) => (
                <div
                  key={transaction?.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center space-x-4">
                    <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                      <Calendar size={20} className="text-gray-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">
                        Monthly Subscription
                      </p>
                      <p className="text-sm text-gray-600">
                        {new Date(transaction?.date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <p className="font-medium text-gray-800">
                        £{transaction?.amount?.toFixed(2)}
                      </p>
                      <p className="text-sm text-gray-600">
                        Card ending in {transaction?.cardLast4}
                      </p>
                    </div>
                    <div
                      className={`px-3 py-1 rounded-full text-sm ${transaction?.status === 'complete' || transaction?.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : transaction?.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                        }`}
                    >
                      {transaction?.status?.charAt(0).toUpperCase() + transaction?.status?.slice(1)}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center text-gray-500">
                <Calendar size={48} className="mb-4 text-gray-300" />
                <p className="text-lg font-medium">No transactions found</p>
                <p className="text-sm">Your transactions will appear here once available.</p>
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                <CreditCard size={16} className="text-gray-600" />
              </div>
              <div>
                <p className="font-medium text-gray-800">Payment Method</p>
                <p className="text-sm text-gray-600">
                  {lastPaymentBrand && lastPaymentCard
                    ? `${lastPaymentBrand?.charAt(0).toUpperCase()}${lastPaymentBrand.slice(1)} ending in ${lastPaymentCard?.trim() || "****"}`
                    : "N/A"}
                </p>
              </div>
            </div>
            {user?.stripeCustomerId && <form action="https://subscriptionservice-bcnavzhxvq-uc.a.run.app/subscription/create-portal-session" method="POST" target="_blank">
              <input type="hidden" name="customer_id" value={user?.stripeCustomerId} />
              <button type="submit" className="text-orange-500 hover:text-orange-600 font-medium">
                Manage Subscription
              </button>
            </form>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionHistory;